import { IconChevronBottomFilled } from '@chilipiper/icons/figma-generated/src/icon'
import React, { useRef } from 'react'
import { useButton } from 'react-aria'
import styled from 'styled-components'
import { LayoutProps, PositionProps, layout, position } from 'styled-system'
import { Icon } from '../../../../old/icon'
import { Box } from '../../../core-components'
import { AriaProps } from '../../types'

type Variant = 'regular' | 'nested'

type Props = {
  'data-test-id'?: string
  onIncrement: AriaProps<'button'>
  onDecrement: AriaProps<'button'>
  variant?: Variant
}

export const InputNumberArrows = ({
  'data-test-id': testId,
  onIncrement,
  onDecrement,
  variant = 'regular',
}: Props) => {
  return (
    <ArrowsWrapper variant={variant}>
      <Button
        variant={variant}
        ariaProps={onIncrement}
        data-test-id={testId && `${testId}-button-up`}
      />
      <Button
        variant={variant}
        ariaProps={onDecrement}
        data-test-id={testId && `${testId}-button-down`}
      />
    </ArrowsWrapper>
  )
}

const Button = ({
  ariaProps,
  'data-test-id': testId,
  variant,
}: {
  ariaProps: AriaProps<'button'>
  'data-test-id'?: string
  variant: Variant
}) => {
  const ref = useRef<HTMLButtonElement>(null)
  const { buttonProps } = useButton(ariaProps, ref)
  return (
    <ArrowButton {...buttonProps} variant={variant} data-test-id={testId} ref={ref}>
      <Icon icon={IconChevronBottomFilled} w={4} h={4} color='icon/action-minor' />
    </ArrowButton>
  )
}

const ArrowButton = styled.button<PositionProps & LayoutProps & { variant: Variant }>`
  ${position}
  ${layout}
   background: transparent;
  height: 50%;
  width: ${({ theme }) => theme.space[7]};
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  border-radius: ${({ theme, variant }) =>
    variant === 'regular' ? `0 ${theme.radii[10]} 0 0` : undefined};
  box-shadow: ${({ theme }) => theme.shadows['border/row']};

  transition:
    box-shadow 0.2s ease-in-out,
    background 0.2s ease-in-out;

  :first-child svg {
    transform: rotate(180deg);
  }
  :last-child {
    border-radius: ${({ theme, variant }) =>
      variant === 'regular' ? `0 0 ${theme.radii[10]} 0` : undefined};
    box-shadow: none;
  }

  :hover {
    background: ${({ theme }) => theme.colors['bg/form-item-hover']};
    box-shadow: none;
  }

  :disabled {
    background: ${({ theme }) => theme.colors['bg/disabled']};
    box-shadow: none;
    svg {
      color: ${({ theme }) => theme.colors['icon/disabled']};
    }
  }
`

const ArrowsWrapper = styled(Box)<{ variant: Variant }>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: ${({ theme }) => theme.space[7]};
  border-radius: ${({ theme, variant }) =>
    variant === 'regular' ? `0 ${theme.radii[10]} ${theme.radii[10]} 0` : undefined};
  box-shadow: ${({ theme }) => theme.shadows['border/cell-left']};

  ${ArrowButton} {
    :active:enabled {
      background: ${({ theme }) => theme.colors['bg/form-item-focused']};
      box-shadow: ${({ theme, variant }) =>
        theme.shadows[
          variant === 'regular' ? 'border/form-item-focused' : 'border/form-item-nested-focused'
        ]};
    }
  }
`
